import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { LayoutHeader } from "@/Header";
import PrivacyPolicyContent from "@/common/PrivacyPolicyContent";
import TermsOfServiceContent from "@/common/TermsOfServiceContent";
import { TERMS_CONTENT_CLASS } from "@/common/TermsDocContent";
import { sanitizeTermsHtml } from "%/sanitizeHtml";
import {
  getTermsDocuments,
  getTermsHistory,
  getTermsVersion,
  TERMS_TYPE_LABEL,
  type TermsDocument,
  type TermsTargetRole,
  type TermsType,
} from "~/services/common/terms";

/**
 * 약관 및 정책 열람 — 비로그인 접근 가능한 단일 원본 페이지.
 *
 *  /policy?type=TERMS_TYPE_SERVICE&seq=12
 *
 *  - 보호자앱/사업자앱은 빌드가 분리되어 있으므로 대상(보호자·사업자)은 화면에서 고르지 않는다.
 *    VITE_APP_TYPE 으로 결정되고, 상단 탭은 문서 종류(이용약관·개인정보처리방침)에 쓴다.
 *  - 마이페이지·가입 화면·개정 고지 배너 모두 이 페이지로 딥링크한다.
 *  - 아직 DB 에 등록되지 않은 유형은 기존 하드코딩 본문으로 폴백한다
 *    (이용약관은 원문 텍스트가 없어 SVG 이미지로만 존재).
 */

interface PolicySearch {
  type?: string;
  seq?: number;
}

export const Route = createFileRoute("/policy")({
  validateSearch: (search: Record<string, unknown>): PolicySearch => ({
    type: typeof search.type === "string" ? search.type : undefined,
    seq: search.seq != null ? Number(search.seq) : undefined,
  }),
  component: RouteComponent,
});

/** 앱 빌드 종류가 곧 약관 대상 — 사업자앱은 사업자 약관만 본다 */
const APP_ROLE: TermsTargetRole =
  import.meta.env.VITE_APP_TYPE === "business" ? "BUSINESS" : "GUARDIAN";

/** DB 에 아직 없는 유형을 위한 레거시 탭 */
const LEGACY_TYPES: TermsType[] = ["TERMS_TYPE_SERVICE", "TERMS_TYPE_PRIVACY"];

const formatDate = (dt?: string | null) => (dt ? dt.slice(0, 10) : "-");

function RouteComponent() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const role = APP_ROLE;
  const [type, setType] = useState<TermsType | null>(
    (search.type as TermsType) ?? null,
  );
  const [seq, setSeq] = useState<number | null>(search.seq ?? null);

  const { data: documents, isLoading: isLoadingDocs } = useQuery({
    queryKey: ["terms-documents", role],
    queryFn: () => getTermsDocuments(role),
    staleTime: 5 * 60 * 1000,
  });

  /**
   * 노출할 유형 탭 = 레거시 2종(이용약관·개인정보처리방침) + DB 에 등록된 유형.
   * 이용약관이 아직 DB 에 없어도 탭은 유지해 레거시 본문을 보여준다.
   */
  const typeTabs = useMemo<TermsType[]>(
    () =>
      Array.from(
        new Set([...LEGACY_TYPES, ...(documents ?? []).map((d) => d.termsType)]),
      ),
    [documents],
  );

  const activeType = type ?? typeTabs[0] ?? "TERMS_TYPE_SERVICE";

  const currentDoc = documents?.find((d) => d.termsType === activeType) ?? null;

  const { data: history } = useQuery({
    queryKey: ["terms-history", role, activeType],
    queryFn: () => getTermsHistory(role, activeType),
    enabled: !!currentDoc,
    staleTime: 5 * 60 * 1000,
  });

  /** 선택된 버전 (미선택이면 현행) */
  const selectedSeq = seq ?? currentDoc?.id ?? null;

  const { data: doc, isLoading: isLoadingDoc } = useQuery({
    queryKey: ["terms-version", selectedSeq],
    queryFn: () => getTermsVersion(selectedSeq as number),
    enabled: selectedSeq != null,
    staleTime: 5 * 60 * 1000,
  });

  // URL 동기화 — 공유·딥링크가 그대로 재현되도록
  useEffect(() => {
    navigate({
      to: "/policy",
      search: {
        type: activeType,
        seq: selectedSeq ?? undefined,
      },
      replace: true,
    });
  }, [activeType, selectedSeq, navigate]);

  const handleTypeChange = (next: TermsType) => {
    setType(next);
    setSeq(null);
  };

  const scheduled = history?.find((v) => v.status === "SCHEDULED") ?? null;
  const isLegacyFallback = !currentDoc && !isLoadingDocs;

  return (
    <>
      <LayoutHeader title="약관 및 정책" hasBackBtn hasFootPrint={false} />

      <div className="flex flex-col flex-1 min-h-0 bg-white">
        {/* 문서 탭 — 이용약관 / 개인정보처리방침 (대상은 앱 빌드로 이미 결정됨) */}
        <div className="flex border-b border-gray-200">
          {typeTabs.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => handleTypeChange(t)}
              className={`flex-1 whitespace-nowrap py-3 text-sm font-bold border-b-2 transition-colors ${
                activeType === t
                  ? "border-orange-400 text-orange-500"
                  : "border-transparent text-gray-400"
              }`}
            >
              {TERMS_TYPE_LABEL[t]}
            </button>
          ))}
        </div>

        <div className="overflow-y-auto flex-1 px-4 py-4">
          {/* 시행 예정 안내 */}
          {scheduled && scheduled.id !== selectedSeq && (
            <button
              type="button"
              onClick={() => setSeq(scheduled.id)}
              className="mb-3 w-full rounded-lg bg-amber-50 px-3 py-2.5 text-left text-xs text-amber-800"
            >
              <span className="font-bold">
                {formatDate(scheduled.effectiveAt)} 시행 예정
              </span>
              <span className="ml-1">개정 약관이 있습니다. 눌러서 확인 →</span>
            </button>
          )}

          {/* 시행일(버전) 선택 */}
          {history && history.length > 0 && (
            <div className="flex items-center gap-2 mb-4">
              <span className="text-xs text-gray-500 shrink-0">시행일</span>
              <select
                value={selectedSeq ?? ""}
                onChange={(e) => setSeq(Number(e.target.value))}
                className="flex-1 rounded-lg border border-gray-200 px-2 py-1.5 text-sm"
              >
                {history.map((v) => (
                  <option key={v.id} value={v.id}>
                    {formatDate(v.effectiveAt)} ({v.termsVersion})
                    {v.status === "ACTIVE"
                      ? " · 현행"
                      : v.status === "SCHEDULED"
                        ? " · 시행 예정"
                        : ""}
                  </option>
                ))}
              </select>
            </div>
          )}

          {isLoadingDocs || isLoadingDoc ? (
            <p className="py-10 text-sm text-center text-gray-400">
              불러오는 중...
            </p>
          ) : doc ? (
            <TermsBody doc={doc} />
          ) : isLegacyFallback ? (
            <LegacyBody type={activeType} />
          ) : (
            <p className="py-10 text-sm text-center text-gray-400">
              등록된 약관이 없습니다.
            </p>
          )}
        </div>
      </div>
    </>
  );
}

function TermsBody({ doc }: { doc: TermsDocument }) {
  return (
    <>
      <div className="mb-3">
        <h1 className="text-base font-bold">{doc.termsTitle}</h1>
        <p className="mt-0.5 text-xs text-gray-500">
          {doc.termsVersion} · 시행일 {formatDate(doc.effectiveAt)}
          {doc.status === "SCHEDULED" && (
            <span className="ml-1 font-bold text-amber-600">시행 예정</span>
          )}
          {doc.status === "ARCHIVED" && (
            <span className="ml-1 text-gray-400">이전 버전</span>
          )}
        </p>
      </div>

      {doc.changeSummary && (
        <div className="mb-4 rounded-lg bg-gray-50 px-3 py-2.5">
          <p className="mb-1 text-xs font-bold text-gray-700">변경 요약</p>
          <p className="text-xs whitespace-pre-wrap text-gray-600">
            {doc.changeSummary}
          </p>
        </div>
      )}

      {doc.termsUrl && (
        <a
          href={doc.termsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="block mb-3 text-xs text-orange-500 underline"
        >
          원문 파일 보기
        </a>
      )}

      {/* 표는 [&_table]:overflow-x-auto 로 자기 영역 안에서만 가로 스크롤된다 */}
      <div
        className={TERMS_CONTENT_CLASS}
        dangerouslySetInnerHTML={{
          __html: sanitizeTermsHtml(doc.contentHtml ?? ""),
        }}
      />
    </>
  );
}

/** DB 이관 전까지의 폴백 — 기존 가입 화면이 쓰던 하드코딩 본문 그대로 */
function LegacyBody({ type }: { type: TermsType }) {
  if (type === "TERMS_TYPE_PRIVACY") {
    return <PrivacyPolicyContent />;
  }
  if (type === "TERMS_TYPE_SERVICE") {
    return <TermsOfServiceContent />;
  }
  return (
    <p className="py-10 text-sm text-center text-gray-400">
      등록된 약관이 없습니다.
    </p>
  );
}
